import jsPDF from 'jspdf'

export async function generateBusinessReport(businessData: any, scoreHistory: any[], redFlags: any[] = []) {
  const pdf = new jsPDF()
  const pageWidth = pdf.internal.pageSize.getWidth()
  const pageHeight = pdf.internal.pageSize.getHeight()
  let yPosition = 20

  // Helper function to add new page if needed
  const checkPageBreak = (requiredSpace: number) => {
    if (yPosition + requiredSpace > pageHeight - 20) {
      pdf.addPage()
      yPosition = 20
      return true
    }
    return false
  }

  // Title
  pdf.setFontSize(20)
  pdf.setFont('helvetica', 'bold')
  pdf.text('Business Health Report', pageWidth / 2, yPosition, { align: 'center' })
  yPosition += 15

  // Business Name
  pdf.setFontSize(16)
  pdf.text(businessData.business_name, pageWidth / 2, yPosition, { align: 'center' })
  yPosition += 10

  // Report Date
  pdf.setFontSize(10)
  pdf.setFont('helvetica', 'normal')
  pdf.text(`Generated: ${new Date().toLocaleDateString('en-IN')}`, pageWidth / 2, yPosition, { align: 'center' })
  yPosition += 15

  // Score Summary Box
  checkPageBreak(40)
  pdf.setFillColor(240, 240, 240)
  pdf.rect(15, yPosition, pageWidth - 30, 35, 'F')
  
  pdf.setFontSize(12)
  pdf.setFont('helvetica', 'bold')
  pdf.text('TLBH Score Summary', 20, yPosition + 10)
  
  pdf.setFontSize(24)
  const score = parseFloat(businessData.score || '0')
  pdf.text(score.toFixed(1), 20, yPosition + 25)
  
  pdf.setFontSize(14)
  pdf.text(`Band: ${businessData.band || 'N/A'}`, 60, yPosition + 25)
  
  pdf.setFontSize(10)
  pdf.setFont('helvetica', 'normal')
  const confidence = parseFloat(businessData.confidence || '85')
  pdf.text(`Confidence: ${confidence.toFixed(0)}%`, 110, yPosition + 25)
  
  yPosition += 45

  // Business Information Section
  checkPageBreak(60)
  pdf.setFontSize(14)
  pdf.setFont('helvetica', 'bold')
  pdf.text('Business Information', 15, yPosition)
  yPosition += 8

  pdf.setFontSize(10)
  pdf.setFont('helvetica', 'normal')
  
  const businessInfo = [
    ['PAN:', businessData.pan],
    ['GSTIN:', businessData.gstin || 'N/A'],
    ['Business Type:', businessData.business_type],
    ['Industry:', businessData.industry_sector || 'N/A'],
    ['Registration Date:', new Date(businessData.registration_date).toLocaleDateString('en-IN')],
    ['Contact Email:', businessData.contact_email || 'N/A'],
    ['Contact Phone:', businessData.contact_phone || 'N/A'],
    ['Address:', businessData.registered_address || 'N/A']
  ]
  
  businessInfo.forEach(([label, value]) => {
    checkPageBreak(7)
    pdf.setFont('helvetica', 'bold')
    pdf.text(label, 20, yPosition)
    pdf.setFont('helvetica', 'normal')
    pdf.text(value, 70, yPosition)
    yPosition += 7
  })
  
  yPosition += 10

  // Score History Section
  if (scoreHistory && scoreHistory.length > 0) {
    checkPageBreak(60)
    pdf.setFontSize(14)
    pdf.setFont('helvetica', 'bold')
    pdf.text('Score History (Last 12 Months)', 15, yPosition)
    yPosition += 10
    
    pdf.setFontSize(9)
    pdf.setFont('helvetica', 'bold')
    pdf.text('Date', 20, yPosition)
    pdf.text('Score', 70, yPosition)
    pdf.text('Band', 110, yPosition)
    pdf.text('Change', 140, yPosition)
    yPosition += 5
    
    pdf.setFont('helvetica', 'normal')
    scoreHistory.slice(0, 12).forEach((item, index) => {
      checkPageBreak(7)
      const date = new Date(item.timestamp).toLocaleDateString('en-IN', { 
        year: 'numeric', 
        month: 'short' 
      })
      const prevScore = index < scoreHistory.length - 1 ? scoreHistory[index + 1].score : item.score
      const change = item.score - prevScore
      const changeText = change !== 0 ? `${change > 0 ? '+' : ''}${change.toFixed(1)}` : 'No change'
      
      pdf.text(date, 20, yPosition)
      pdf.text(item.score.toFixed(1), 70, yPosition)
      pdf.text(item.band, 110, yPosition)
      pdf.text(changeText, 140, yPosition)
      yPosition += 6
    })
    
    yPosition += 10
  }

  // Summary Statistics
  if (scoreHistory && scoreHistory.length > 0) {
    checkPageBreak(40)
    pdf.setFontSize(14)
    pdf.setFont('helvetica', 'bold')
    pdf.text('Summary Statistics', 15, yPosition)
    yPosition += 10
    
    const currentScore = scoreHistory[0]?.score || 0
    const avgScore = scoreHistory.reduce((sum, item) => sum + item.score, 0) / scoreHistory.length
    const oldestScore = scoreHistory[scoreHistory.length - 1]?.score || 0
    const totalChange = currentScore - oldestScore
    
    pdf.setFontSize(10)
    pdf.setFont('helvetica', 'normal')
    
    const stats = [
      ['Current Score:', currentScore.toFixed(1)],
      ['Average Score:', avgScore.toFixed(1)],
      ['12-Month Change:', `${totalChange > 0 ? '+' : ''}${totalChange.toFixed(1)}`],
      ['Trend:', totalChange > 1 ? 'Improving' : totalChange < -1 ? 'Declining' : 'Stable']
    ]
    
    stats.forEach(([label, value]) => {
      checkPageBreak(7)
      pdf.setFont('helvetica', 'bold')
      pdf.text(label, 20, yPosition)
      pdf.setFont('helvetica', 'normal')
      pdf.text(value, 70, yPosition)
      yPosition += 7
    })
  }

  // Recent Alerts Section
  if (redFlags && redFlags.length > 0) {
    yPosition += 10
    checkPageBreak(60)
    pdf.setFontSize(14)
    pdf.setFont('helvetica', 'bold')
    pdf.text('Recent Alerts & Red Flags', 15, yPosition)
    yPosition += 10
    
    const alertsToShow = redFlags.slice(0, 10) // Show top 10 alerts
    
    alertsToShow.forEach((flag) => {
      checkPageBreak(25)
      
      // Alert box
      const getSeverityColor = (impact: number): [number, number, number] => {
        if (impact >= 15) return [254, 226, 226] // red-100
        if (impact >= 10) return [255, 237, 213] // orange-100
        return [254, 249, 195] // yellow-100
      }
      
      const color = getSeverityColor(flag.scoreImpact)
      pdf.setFillColor(color[0], color[1], color[2])
      pdf.rect(15, yPosition, pageWidth - 30, 20, 'F')
      
      pdf.setFontSize(10)
      pdf.setFont('helvetica', 'bold')
      const category = flag.category.replace(/_/g, ' ').toUpperCase()
      pdf.text(category, 20, yPosition + 6)
      
      // Severity badge
      const getSeverityText = (impact: number) => {
        if (impact >= 15) return 'CRITICAL'
        if (impact >= 10) return 'HIGH'
        if (impact >= 5) return 'MEDIUM'
        return 'LOW'
      }
      
      pdf.setFontSize(8)
      pdf.text(`[${getSeverityText(flag.scoreImpact)}]`, pageWidth - 40, yPosition + 6)
      
      pdf.setFontSize(9)
      pdf.setFont('helvetica', 'normal')
      const conditionText = pdf.splitTextToSize(flag.condition, pageWidth - 40)
      pdf.text(conditionText, 20, yPosition + 12)
      
      yPosition += 22
    })
    
    if (redFlags.length > 10) {
      pdf.setFontSize(9)
      pdf.setFont('helvetica', 'italic')
      pdf.text(`... and ${redFlags.length - 10} more alerts`, 20, yPosition)
      yPosition += 8
    }
  }

  // Footer
  const totalPages = pdf.internal.pages.length - 1
  for (let i = 1; i <= totalPages; i++) {
    pdf.setPage(i)
    pdf.setFontSize(8)
    pdf.setFont('helvetica', 'normal')
    pdf.text(
      `Page ${i} of ${totalPages} | TrustLink Business Health Score System`,
      pageWidth / 2,
      pageHeight - 10,
      { align: 'center' }
    )
  }

  // Save the PDF
  const fileName = `${businessData.business_name.replace(/[^a-z0-9]/gi, '_')}_Report_${new Date().toISOString().split('T')[0]}.pdf`
  pdf.save(fileName)
}
